import React, { useState } from 'react';
import {
    Container,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    Divider,
    Link,
    makeStyles
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Mail, Lock, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHistory, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    card: {
        maxWidth: 400,
        width: '100%',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(3),
    },
    title: {
        fontWeight: 700,
        color: '#333',
        marginBottom: theme.spacing(1),
    },
    subtitle: {
        color: '#666',
        fontSize: '0.9rem',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    input: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
        },
    },
    loginButton: {
        padding: theme.spacing(1.5),
        borderRadius: 12,
        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
        color: 'white',
        fontWeight: 600,
        '&:hover': {
            background: 'linear-gradient(45deg, #667eea 60%, #764ba2 100%)',
        },
        '&:disabled': {
            background: '#ccc',
        },
    },
    googleButton: {
        padding: theme.spacing(1.5),
        borderRadius: 12,
        border: '2px solid #ddd',
        color: '#333',
        fontWeight: 600,
        '&:hover': {
            borderColor: '#bbb',
            backgroundColor: '#f5f5f5',
        },
    },
    divider: {
        margin: theme.spacing(2, 0),
        color: '#666',
        textAlign: 'center',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: '#ddd',
            zIndex: 0
        },
        '& .MuiTypography-root': {
            backgroundColor: 'white',
            padding: theme.spacing(0, 2),
            position: 'relative',
            zIndex: 1
        }
    },
    footer: {
        textAlign: 'center',
        marginTop: theme.spacing(3),
        color: '#666',
    },
    link: {
        color: '#667eea',
        cursor: 'pointer',
        textDecoration: 'none',
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
}));

const Login = () => {
    const classes = useStyles();
    const { login, loginWithGoogle } = useAuth();
    const history = useHistory();
    const location = useLocation();
    
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const { from } = location.state || { from: { pathname: '/guide' } };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await login(formData.email, formData.password);
            history.replace(from);
        } catch (error) {
            setError(getErrorMessage(error.code));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');

        try {
            await loginWithGoogle();
            history.replace(from);
        } catch (error) {
            setError('Failed to sign in with Google');
        } finally {
            setLoading(false);
        }
    };

    const getErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/user-not-found':
                return 'No account found with this email address.';
            case 'auth/wrong-password':
                return 'Incorrect password. Please try again.';
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/too-many-requests':
                return 'Too many failed attempts. Please try again later.';
            default:
                return 'An error occurred. Please try again.';
        }
    };

    const goToSignup = () => {
        history.push('/signup', { from });
    };

    return (
        <Box className={classes.root}>
            <Container maxWidth="sm">
                <Card className={classes.card}>
                    <CardContent style={{ padding: '2rem' }}>
                        <Box className={classes.header}>
                            <Typography variant="h4" className={classes.title}>
                                Welcome to InstaWorthy
                            </Typography>
                            <Typography variant="body2" className={classes.subtitle}>
                                Create amazing AI-generated profile pictures
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error" style={{ marginBottom: '1rem' }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className={classes.form}>
                            <TextField
                                name="email"
                                type="email"
                                label="Email Address"
                                variant="outlined"
                                fullWidth
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: <Mail size={20} style={{ marginRight: 8, color: '#666' }} />
                                }}
                            />

                            <TextField
                                name="password"
                                type="password"
                                label="Password"
                                variant="outlined"
                                fullWidth
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className={classes.input}
                                InputProps={{
                                    startAdornment: <Lock size={20} style={{ marginRight: 8, color: '#666' }} />
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                className={classes.loginButton}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <LogIn size={20} />}
                            >
                                {loading ? 'Signing In...' : 'Sign In'}
                            </Button>
                        </form>

                        <Box className={classes.divider}>
                            <Typography variant="body2">or</Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            fullWidth
                            className={classes.googleButton}
                            onClick={handleGoogleLogin}
                            disabled={loading}
                        >
                            <img 
                                src="https://developers.google.com/identity/images/g-logo.png" 
                                alt="Google" 
                                style={{ width: 20, height: 20, marginRight: 8 }}
                            />
                            Continue with Google
                        </Button>

                        <Box className={classes.footer}>
                            <Typography variant="body2" style={{ marginBottom: '1rem' }}>
                                Don't have an account?{' '}
                                <Link className={classes.link} onClick={goToSignup}>
                                    Create one free
                                </Link>
                            </Typography>
                            <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#888' }}>
                                🎉 New users get extra tokens to create photos!
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default Login;